import { readonly, ref } from 'vue'

/**
 * Shared reactive "now", ticking once a minute.
 *
 * Retention math (`getChatDeletionInfo`, `groupChatsByRelativeDate`) reads the current time,
 * but calling `new Date()` inside a `computed` freezes that value until an unrelated reactive
 * dependency changes — so a chat could silently cross into the deletion window without the
 * sidebar "Expiring soon" section or the inline retention banner ever updating. Reading this
 * ref makes those computeds re-evaluate as wall-clock time advances.
 *
 * Minute granularity is enough: the finest thing it drives is a whole-day countdown. A single
 * app-lifetime interval is shared across every consumer, started lazily on first use so merely
 * importing this module has no side effect (keeps unit tests that pass an explicit `now` clean).
 */
const RETENTION_TICK_MS = 60_000

const now = ref(new Date())
let started = false

function ensureStarted(): void {
  if (started) return
  started = true
  now.value = new Date()
  setInterval(() => {
    now.value = new Date()
  }, RETENTION_TICK_MS)
}

/** Reactive current time for retention/expiry computeds. Read `.value` where a `now` is needed. */
export function useRetentionClock() {
  ensureStarted()
  return readonly(now)
}
